import {
  DEFAULT_TIME_DISPLAY_MODE,
  TimeDisplayMode,
} from "@/shared/state/visualization/schema/v1/v1";
import {
  Duration,
  nanoSecToLocalTimestamp,
  nanoSecToSecStr,
  nanoSecToUTCTimestamp,
} from "@/shared/time";

// The enum lives with the v1 viz schema because its string values are persisted
// vocabulary (`settings.timeDisplayMode` in stored workspace configs). It is
// re-exported here so display-layer consumers keep one import site.
export { DEFAULT_TIME_DISPLAY_MODE, TimeDisplayMode };

/**
 * Shared formatter for elapsed time display with nanosecond precision.
 * Note: Existential check can be removed when we upgrade to Node 23.
 */
export const elapsedTimeFormatter =
  "DurationFormat" in Intl
    ? new Intl.DurationFormat("en", {
        style: "digital",
        hours: "2-digit",
        fractionalDigits: 9,
      })
    : null;

export const TIME_DISPLAY_MODES = [
  TimeDisplayMode.Elapsed,
  TimeDisplayMode.ElapsedTime,
  TimeDisplayMode.Log,
  TimeDisplayMode.Local,
  TimeDisplayMode.UTC,
] as const;

// Short labels for the timeline's mode picker and the workspace settings
// dialog, so both surfaces name the modes identically. The seconds variant is
// suffixed "(s)" to distinguish it from the HH:MM:SS variant, which keeps the
// unadorned "Elapsed" label. Decoupled from the enum string values so
// persisted configs (which store the enum values) remain stable.
export const TIME_DISPLAY_MODE_LABELS: Record<TimeDisplayMode, string> = {
  [TimeDisplayMode.Elapsed]: "Elapsed (s)",
  [TimeDisplayMode.ElapsedTime]: "Elapsed",
  [TimeDisplayMode.Log]: "Log",
  [TimeDisplayMode.Local]: "Local",
  [TimeDisplayMode.UTC]: "UTC",
};

/**
 * Formats a timestamp based on the current time display mode.
 *
 * @param time - The timestamp in nanoseconds
 * @param mode - The display mode to use
 * @param earliestTime - The earliest timestamp in the workspace (used for Elapsed mode)
 * @param includeTz - Whether to include the timezone in the output if local time is displayed (default: true)
 * @returns A formatted string representation of the timestamp
 */
export function formatTimeByMode(
  time: bigint,
  mode: TimeDisplayMode,
  earliestTime: bigint | null | undefined,
  includeTz: boolean = true,
): string {
  switch (mode) {
    case TimeDisplayMode.Elapsed:
      return nanoSecToSecStr(time - (earliestTime ?? 0n));
    case TimeDisplayMode.ElapsedTime: {
      // Format elapsed time as HH:MM:SS.nnnnnnnnn using Intl.DurationFormat
      if (!elapsedTimeFormatter) {
        return nanoSecToSecStr(time - (earliestTime ?? 0n));
      }
      const elapsedNanos = time - (earliestTime ?? 0n);
      const duration = Duration.from({ nanoseconds: elapsedNanos });
      return elapsedTimeFormatter.format(duration.toObject());
    }
    case TimeDisplayMode.Log:
      return nanoSecToSecStr(time);
    case TimeDisplayMode.Local: {
      const raw = nanoSecToLocalTimestamp(time);
      // raw is like "2025-12-02 12:04:08.247 PM PST"; the [0, 26] prefix
      // ("2025-12-02 12:04:08.247 PM ") is safe to slice because
      // dateToLocalTimestamp pins the locale to en-US 12-hour, making the
      // pre-timezone shape deterministic across browsers.
      return includeTz ? raw : raw.slice(0, 26);
    }
    case TimeDisplayMode.UTC:
      return nanoSecToUTCTimestamp(time);
  }
}
